import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { alpha, Box } from '@mui/system';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ClearIcon } from '@mui/x-date-pickers';
import TenantInterfaceDialog from '../../tenants/TenantInterfaceDialog';

export default function WeclappInterfaceSettings({ app, deployment, onSave }) {
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [error, setError] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);

  const handleDialogClose = () => setOpenDialog(false);
  const handleDialogSave = () => {
    fetchInterfaces();
    setOpenDialog(false);
  };

  const handleInterfaceChange = (event) => {
    setSelectedInterface(event.target.value);
    setError(!event.target.value);
  };

  const fetchInterfaces = async () => {
    setLoading(true);
    try {
      const response = await tenant.get('/interface');
      const weclappInterfaces = response.data.filter(
        (interf) => interf.type === 'weclapp',
      );
      setInterfaces(weclappInterfaces);

      if (deployment?.config?.weclappInterface) {
        const foundInterface = weclappInterfaces.find(
          (interf) => interf._id === deployment.config.weclappInterface,
        );
        setSelectedInterface(foundInterface || null);
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          message="Die Weclapp Interfaces konnten nicht geladen werden"
        />,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterfaces();
  }, [deployment?.config?.weclappInterface]);

  const handleSave = async () => {
    if (!selectedInterface) {
      setError(true);
      return;
    }
    onSave({ weclappInterface: selectedInterface._id });
  };

  return (
    <Box
      sx={{
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">weclapp-Anbindung</Typography>
        <Divider />
      </Box>
      {interfaces.length === 0 ? (
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Es wurde noch kein Weclapp Interface konfiguriert
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenDialog(true)}
            >
              Neues Interface erstellen
            </Button>

            <Tooltip title="Interface Liste aktualisieren">
              <IconButton
                size="small"
                onClick={fetchInterfaces}
                disabled={loading}
              >
                <RefreshIcon
                  sx={{
                    animation: loading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
              Wählen Sie das Weclapp-Interface aus, das für{' '}
              {app?.title || deployment?.title} verwendet werden soll.
            </Alert>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenDialog(true)}
            >
              Neues Interface erstellen
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="interface-select-label">Interface</InputLabel>
              <Select
                labelId="interface-select-label"
                id="interface-select"
                value={selectedInterface || ''}
                onChange={handleInterfaceChange}
                input={<OutlinedInput label="Interface" />}
                renderValue={(selected) => selected.description}
                endAdornment={
                  selectedInterface ? (
                    <IconButton
                      size="small"
                      sx={{ mr: 2 }}
                      onClick={() => setSelectedInterface(null)}
                      aria-label="Interface löschen"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              >
                {interfaces.map((interf) => (
                  <MenuItem key={interf._id} value={interf}>
                    {interf.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Interface Liste aktualisieren">
                <IconButton
                  size="small"
                  onClick={fetchInterfaces}
                  disabled={loading}
                >
                  <RefreshIcon
                    sx={{
                      animation: loading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {selectedInterface && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Ausgewähltes Interface:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.7),
                }}
              >
                <Chip
                  label={selectedInterface.description || 'Kein Name verfügbar'}
                  variant="outlined"
                  color="primary"
                />
              </Box>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ mt: 'auto', ml: 'auto' }} // Pushes to bottom right
            disabled={loading || !selectedInterface}
          >
            Speichern
          </Button>
        </>
      )}

      {error && (
        <Typography
          color="error"
          variant="caption"
          sx={{ display: 'block', mt: 1 }}
        >
          Bitte wählen Sie ein Interface aus
        </Typography>
      )}
      {openDialog && (
        <TenantInterfaceDialog
          open={openDialog}
          onClose={handleDialogClose}
          defaultType="weclapp"
          onSave={handleDialogSave}
        />
      )}
    </Box>
  );
}
